import * as React from 'react';
import {
  HiOutlineArrowUpRight,
  HiOutlineChartBar,
  HiOutlineCursorArrowRays,
  HiOutlineGlobeAlt,
  HiOutlineSquares2X2,
} from 'react-icons/hi2';
import type {
  FlowAnalysisHourlyInsight,
  FlowAnalysisOutlook,
  FlowAnalysisPlay,
  FlowAnalysisReport,
  FlowAnalysisStrength,
  FlowAnalysisTrade,
  FlowAnalysisTrend,
} from 'constants/payloads/flowAnalysis';
import {
  etStampDateTime,
  formatDaysToExpiry,
  formatEtStamp,
  formatExpiry,
  formatMoneyness,
  formatPremium,
  formatSizeVsOi,
  playHref,
  tickerFlowHref,
} from 'layouts/FlowPageContainer/flowAiAnalysisReportFormat';

interface Props {
  report: FlowAnalysisReport;
}

export type FlowAnalysisSectionId =
  | 'market-trends'
  | 'hourly-insights'
  | 'noteworthy-trades'
  | 'options-plays';

/**
 * The four report sections, in order. Ids match the ones the markdown headings produced, so the
 * sidebar and its scroll tracking work the same for both renderers.
 */
export const FLOW_ANALYSIS_REPORT_SECTIONS: { id: FlowAnalysisSectionId; title: string }[] = [
  { id: 'market-trends', title: 'Market Trends' },
  { id: 'hourly-insights', title: 'Hourly Insights' },
  { id: 'noteworthy-trades', title: 'Noteworthy Trades' },
  { id: 'options-plays', title: 'Options Plays' },
];

// Match the dashboard's SectionTitle: a small green type icon beside a white heading.
const SECTION_ICONS: Record<FlowAnalysisSectionId, typeof HiOutlineGlobeAlt> = {
  'market-trends': HiOutlineGlobeAlt,
  'hourly-insights': HiOutlineChartBar,
  'noteworthy-trades': HiOutlineCursorArrowRays,
  'options-plays': HiOutlineSquares2X2,
};

/** Chip text colors, all from the app palette and at least 4.5:1 on palette-black-4. */
type Tone = 'bullish' | 'bearish' | 'gray' | 'white' | 'yellow' | 'topPosition';

const TONES: Record<Tone, string> = {
  bullish: 'text-call-text',
  bearish: 'text-put-text',
  gray: 'text-palette-black-7',
  white: 'text-white',
  yellow: 'text-graph-yellow',
  topPosition: 'text-top-position',
};

// Direction in the flow cards' colors: calls green, puts red, no direction gray.
const OUTLOOK_TONE: Record<FlowAnalysisOutlook, Tone> = {
  Bullish: 'bullish',
  Bearish: 'bearish',
  Neutral: 'gray',
};
// Golden Sweep in gold and Top Position in the app's own Top Position color; the rest stay gray.
const BADGE_TONE: Record<string, Tone> = {
  'Golden Sweep': 'yellow',
  'Top Position': 'topPosition',
};

function Chip({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border border-palette-border bg-palette-black-4 px-2 py-0.5 text-[11px] font-semibold leading-4 ${TONES[tone]}`}
    >
      {children}
    </span>
  );
}

/** White, as in the guide: Strength has no direction. */
function Strength({ value }: { value: FlowAnalysisStrength }) {
  return (
    <span
      role="img"
      aria-label={`Strength ${value} of 5`}
      className="inline-flex items-center gap-1.5"
    >
      <span aria-hidden="true" className="inline-flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((dot) => (
          <span
            key={dot}
            className={`h-2 w-2 rounded-full ${dot <= value ? 'bg-white' : 'bg-white/15'}`}
          />
        ))}
      </span>
      <span
        aria-hidden="true"
        className="text-[11px] font-semibold tabular-nums text-text-secondary"
      >
        {value}/5
      </span>
    </span>
  );
}

/** Real spaces between parts, so screen readers and copied text keep the words apart. */
const spaced = (children: React.ReactNode) =>
  React.Children.toArray(children).flatMap((child, index) => (index ? [' ', child] : [child]));

function Badges({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">{spaced(children)}</div>
  );
}

function Meta({ children }: { children: React.ReactNode }) {
  return <span className="text-xs tabular-nums text-text-secondary">{children}</span>;
}

function Detail({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <p className="mt-2 text-sm leading-6 text-text-secondary">
      {label && <span className="font-semibold text-white">{label}</span>}
      {label && ' '}
      {children}
    </p>
  );
}

const ITEM = 'py-4 first:pt-3';
const HEADLINE = 'text-[15px] font-semibold leading-6 text-white';
/** White and underlined like the markdown's links; the focus ring is the guide's. */
const LINK =
  'rounded-sm text-white underline decoration-white/30 underline-offset-4 hover:decoration-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue';

function TrendSummary({ trend, runAt }: { trend: FlowAnalysisTrend; runAt: string }) {
  return (
    <>
      <h4 className={HEADLINE}>{trend.headline}</h4>
      <Badges>
        <Chip tone={OUTLOOK_TONE[trend.outlook]}>{trend.outlook}</Chip>
        <Strength value={trend.strength} />
        <Chip tone="white">{trend.stage}</Chip>
        <Meta>
          First Detected{' '}
          <time
            dateTime={etStampDateTime(trend.firstDetectedEt)}
            title={`${trend.firstDetectedEt} ET`}
          >
            {formatEtStamp(trend.firstDetectedEt, runAt)}
          </time>
        </Meta>
      </Badges>
    </>
  );
}

function Trend({ trend, runAt }: { trend: FlowAnalysisTrend; runAt: string }) {
  return (
    <li className={ITEM}>
      <TrendSummary trend={trend} runAt={runAt} />
      <Detail label="Why">{trend.why}</Detail>
    </li>
  );
}

function HourlySummary({ insight }: { insight: FlowAnalysisHourlyInsight }) {
  return (
    <>
      <h4 className={HEADLINE}>{insight.headline}</h4>
      <Badges>
        <Chip tone="white">{insight.relation}</Chip>
        <Chip tone={OUTLOOK_TONE[insight.outlook]}>{insight.outlook}</Chip>
        <Strength value={insight.strength} />
      </Badges>
    </>
  );
}

function Hourly({ insight }: { insight: FlowAnalysisHourlyInsight }) {
  return (
    <li className={ITEM}>
      <HourlySummary insight={insight} />
      <Detail>{insight.read}</Detail>
      <Detail label="Why">{insight.why}</Detail>
    </li>
  );
}

function TradeSummary({ trade, runAt }: { trade: FlowAnalysisTrade; runAt: string }) {
  const stats = [
    <time key="time" dateTime={etStampDateTime(trade.tradedAtEt)} title={`${trade.tradedAtEt} ET`}>
      {formatEtStamp(trade.tradedAtEt, runAt)}
    </time>,
    trade.sizeVsOi !== null && formatSizeVsOi(trade.sizeVsOi),
    trade.daysToExpiry !== null && formatDaysToExpiry(trade.daysToExpiry),
    trade.otmPercent !== null && formatMoneyness(trade.otmPercent),
  ].filter(Boolean);
  return (
    <>
      <h4 className={`flex flex-wrap items-baseline gap-x-2 ${HEADLINE}`}>
        {spaced([
          <a key="ticker" href={tickerFlowHref(trade.ticker)} className={LINK}>
            {trade.ticker}
          </a>,
          <span key="contract">
            {trade.strike} {trade.side}
          </span>,
          <time
            key="expiry"
            dateTime={trade.expiry
              .split('-')
              .map((part, index) => (index ? part.padStart(2, '0') : part))
              .join('-')}
            title={trade.expiry}
            className="font-medium text-text-secondary"
          >
            {formatExpiry(trade.expiry, runAt)}
          </time>,
          <span key="premium" className="ml-auto tabular-nums" title={trade.premium}>
            {formatPremium(trade.premium)}
          </span>,
        ])}
      </h4>
      <Badges>
        {trade.badges.map((badge) => (
          <Chip key={badge} tone={BADGE_TONE[badge] ?? 'gray'}>
            {badge}
          </Chip>
        ))}
        <Chip tone="white">{trade.intent}</Chip>
        <Strength value={trade.strength} />
        <Meta>
          {stats.map((stat, index) => (
            <React.Fragment key={index}>
              {index > 0 && ' · '}
              {stat}
            </React.Fragment>
          ))}
        </Meta>
      </Badges>
    </>
  );
}

function Trade({ trade, runAt }: { trade: FlowAnalysisTrade; runAt: string }) {
  return (
    <li className={ITEM}>
      <TradeSummary trade={trade} runAt={runAt} />
      <Detail label="Why">{trade.why}</Detail>
    </li>
  );
}

function PlaySummary({ play }: { play: FlowAnalysisPlay }) {
  return (
    <>
      <h4 className={HEADLINE}>
        <a href={playHref(play)} className={`inline-flex items-center gap-1 ${LINK}`}>
          {play.ticker} {play.strategy}
          <HiOutlineArrowUpRight aria-hidden="true" className="h-3.5 w-3.5 text-call-text" />
        </a>
      </h4>
      <Badges>
        <Chip tone={OUTLOOK_TONE[play.outlook]}>{play.outlook}</Chip>
        <Strength value={play.strength} />
      </Badges>
    </>
  );
}

function Play({ play }: { play: FlowAnalysisPlay }) {
  return (
    <li className={ITEM}>
      <PlaySummary play={play} />
      <Detail label="Strategy">{play.strategyText}</Detail>
      <Detail label="Why">{play.why}</Detail>
      {play.catalyst && <Detail label="Catalyst">{play.catalyst}</Detail>}
    </li>
  );
}

interface ExampleProps {
  report: FlowAnalysisReport;
  sectionId: FlowAnalysisSectionId;
}

/** The first item's actual header and facts, shared with the report rather than re-created. */
export function FlowAiAnalysisReportExample({ report, sectionId }: ExampleProps) {
  let summary: React.ReactNode = null;
  if (sectionId === 'market-trends' && report.marketTrends[0])
    summary = <TrendSummary trend={report.marketTrends[0]} runAt={report.generatedAtEt} />;
  if (sectionId === 'hourly-insights' && report.hourlyInsights[0])
    summary = <HourlySummary insight={report.hourlyInsights[0]} />;
  if (sectionId === 'noteworthy-trades' && report.noteworthyTrades[0])
    summary = <TradeSummary trade={report.noteworthyTrades[0]} runAt={report.generatedAtEt} />;
  if (sectionId === 'options-plays' && report.optionsPlays[0])
    summary = <PlaySummary play={report.optionsPlays[0]} />;
  if (!summary) return null;

  return (
    <div
      className="mb-4 rounded-lg border border-palette-border bg-palette-black-3 p-4"
      data-analysis-example={sectionId}
    >
      <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-secondary">
        From this report
      </p>
      {summary}
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: FlowAnalysisSectionId;
  title: string;
  children: React.ReactNode[];
}) {
  const Icon = SECTION_ICONS[id];
  return (
    <section aria-labelledby={`section-${id}`}>
      <h3
        id={`section-${id}`}
        tabIndex={-1}
        className="mb-1 flex items-center gap-3 text-lg font-semibold leading-7 text-white outline-none"
      >
        <Icon aria-hidden="true" className="h-5 w-5 shrink-0 text-call-text" />
        {title}
      </h3>
      {children.length > 0 ? (
        <ul className="divide-y divide-palette-border">{children}</ul>
      ) : (
        <p className="pb-1 pt-3 text-sm text-text-secondary">No high-strength items.</p>
      )}
    </section>
  );
}

/** The structured report: each item as a headline, a badge row and the case behind it. */
export default function FlowAiAnalysisReport({ report }: Props) {
  const runAt = report.generatedAtEt;
  const items: Record<FlowAnalysisSectionId, React.ReactNode[]> = {
    'market-trends': report.marketTrends.map((trend, index) => (
      <Trend key={`${index}-${trend.headline}`} trend={trend} runAt={runAt} />
    )),
    'hourly-insights': report.hourlyInsights.map((insight, index) => (
      <Hourly key={`${index}-${insight.headline}`} insight={insight} />
    )),
    'noteworthy-trades': report.noteworthyTrades.map((trade, index) => (
      <Trade key={`${index}-${trade.header}`} trade={trade} runAt={runAt} />
    )),
    'options-plays': report.optionsPlays.map((play, index) => (
      <Play key={`${index}-${play.ticker}-${play.strategy}`} play={play} />
    )),
  };
  return (
    <div className="space-y-8" data-analysis-report-sections>
      {FLOW_ANALYSIS_REPORT_SECTIONS.map(({ id, title }) => (
        <Section key={id} id={id} title={title}>
          {items[id]}
        </Section>
      ))}
    </div>
  );
}
